import propTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import 'react-phone-number-input/style.css';
import { getNestedErrorMessage } from '@/utils/utils';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import Styled from './phone-input-style';

/**
 * Register options for React Hook Form.
 *
 * @typedef {object} RegisterOptions
 * @property {React.Ref<any>} [ref] - React element ref.
 * @property {boolean | { value: boolean, message: string }} [required] - A Boolean which, if true, indicates that the input must have a value before the form can be submitted. You can assign a string to return an error message in the errors object. Note: This config aligns with web constrained API for required input validation, for object or array type of input use validate function instead.
 * @property {{ value: number, message: string }} [maxLength] - The maximum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [minLength] - The minimum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [max] - The maximum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [min] - The minimum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: RegExp, message: string }} [pattern] - A regular expression to match the input value, and an optional `message` (string) for the error message.
 * @property {(value: any) => boolean | string | Promise<boolean | string>} [validate] - A custom validation function or a function returning a promise resolving to a boolean or string. If a string is returned, it will be used as the error message.
 * @property {boolean} [valueAsNumber] - Whether to treat the value as a number (useful for numeric inputs).
 * @property {boolean} [valueAsDate] - Whether to treat the value as a date.
 * @property {(value: any) => any} [setValueAs] - A function to transform the value before setting it in the form state.
 * @property {boolean} [disabled] - Whether the input is disabled (default: false).
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - A function to handle input changes.
 * @property {(event: React.FocusEvent<HTMLInputElement>) => void} [onBlur] - A function to handle input blur events.
 * @property {any} [value] - The initial value of the input.
 * @property {boolean} [shouldUnregister] - Whether to unregister the input on unmount.
 * @property {string | string[]} [deps] - The names of other fields that affect validation of this input.
 */

/**
 * Phone input component
 * @param {object} props - The root object
 * @param {string} props.name - The name of the input
 * @param {boolean} props.disabled - Whether the input is disabled.
 * @param {RegisterOptions} props.registerOptions - The register options for React Hook Form.
 * @returns {ReactComponent} The phone input component
 */
const PhoneInputComponent = ({ name, disabled, registerOptions }) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage = getNestedErrorMessage(name, errors);

  return (
    <Styled.PhoneContainer aria-disabled={disabled}>
      <Styled.Label error={errorMessage}>Phone Number</Styled.Label>
      <Controller
        defaultValue=""
        name={name}
        control={control}
        rules={registerOptions}
        render={({ field }) => (
          <Styled.PhoneInput
            placeholder="Contact number"
            inputRef={register}
            international
            countryCallingCodeEditable={false}
            defaultCountry="ZA"
            value={field.value}
            id={field.name}
            name={field.name}
            autoComplete="phoneInput"
            onChange={(value) => field.onChange(value)}
            errorMessage={errorMessage}
          />
        )}
      />
      <ValidationErrorMessage message={errorMessage} />
    </Styled.PhoneContainer>
  );
};

PhoneInputComponent.defaultProps = {
  name: '',
  disabled: false,
  registerOptions: {
    onChange: () => {},
  },
};

PhoneInputComponent.propTypes = {
  name: propTypes.string,
  disabled: propTypes.bool,
  registerOptions: propTypes.shape({}),
};

export default PhoneInputComponent;
